import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  CornerDownRight,
  ExternalLink,
  Layers,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCcw,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
  formatQty,
  useUpdateBomLine,
  type BomTreeNode,
  type FlatPart,
} from '@/features/manufacturing/boms';
import { formatCurrency } from '@/lib/formatters';
import { cn } from '@/lib/utils';

const centsOrDash = (cents: number, missing: number) =>
  missing > 0 && cents === 0 ? '—' : formatCurrency(Math.round(cents));

export interface BomNodeActions {
  canEdit: boolean;
  rootItemId: string;
  /** Add components to `node`'s item, or to the root when null. */
  onAddInto: (node: BomTreeNode | null) => void;
  onEdit: (node: BomTreeNode) => void;
  onMove: (node: BomTreeNode, direction: -1 | 1) => void;
  onRemove: (node: BomTreeNode) => void;
}

function QtyCell({ node, canEdit }: { node: BomTreeNode; canEdit: boolean }) {
  const update = useUpdateBomLine();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(node.line.quantity));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setValue(String(node.line.quantity));
  }, [node.line.quantity, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commit = async () => {
    const qty = Number(value);
    setEditing(false);
    if (qty === node.line.quantity) return;
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error('Quantity must be greater than zero.');
      return;
    }
    try {
      await update.mutateAsync({ id: node.line.id, quantity: qty });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update quantity.');
    }
  };

  if (!canEdit) return <span className="tabular-nums">{formatQty(node.line.quantity)}</span>;
  if (editing) {
    return (
      <input
        ref={inputRef}
        value={value}
        inputMode="decimal"
        aria-label={`Quantity of ${node.line.item?.sku ?? 'component'}`}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void commit();
          } else if (e.key === 'Escape') {
            setValue(String(node.line.quantity));
            setEditing(false);
          }
        }}
        className="h-7 w-20 rounded border border-info bg-card px-2 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-info/40"
      />
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      title="Click to edit quantity"
      className={cn(
        'rounded px-2 py-0.5 text-right tabular-nums hover:bg-info-soft/30 hover:text-info',
        update.isPending && 'opacity-50',
      )}
    >
      {formatQty(node.line.quantity)}
    </button>
  );
}

function NodeActionsMenu({ node, actions }: { node: BomTreeNode; actions: BomNodeActions }) {
  const navigate = useNavigate();
  const item = node.line.item;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          aria-label={`Actions for ${item?.sku ?? 'line'}`}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        {actions.canEdit && (
          <>
            <DropdownMenuItem onSelect={() => actions.onAddInto(node)}>
              <Plus className="h-4 w-4" />
              {node.isSubAssembly
                ? 'Add components to this sub-assembly'
                : 'Make sub-assembly (add parts)'}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => actions.onEdit(node)}>
              <Pencil className="h-4 w-4" />
              Edit line
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => actions.onEdit(node)}>
              <RefreshCcw className="h-4 w-4" />
              Swap part…
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={node.siblingIndex === 0}
              onSelect={() => actions.onMove(node, -1)}
            >
              <ArrowUp className="h-4 w-4" />
              Move up
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={node.siblingIndex === node.siblingCount - 1}
              onSelect={() => actions.onMove(node, 1)}
            >
              <ArrowDown className="h-4 w-4" />
              Move down
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {node.isSubAssembly && (
          <DropdownMenuItem onSelect={() => navigate(`/mfg/boms/${node.line.component_item_id}`)}>
            <Layers className="h-4 w-4" />
            Open sub-assembly BOM
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onSelect={() => navigate(`/catalog/items/${node.line.component_item_id}`)}
        >
          <ExternalLink className="h-4 w-4" />
          Open catalog item
        </DropdownMenuItem>
        {actions.canEdit && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-fresh-salmon-dark focus:text-fresh-salmon-dark"
              onSelect={() => actions.onRemove(node)}
            >
              <Trash2 className="h-4 w-4" />
              Remove line
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ItemCell({ node }: { node: BomTreeNode }) {
  const item = node.line.item;
  return (
    <div className="flex min-w-0 items-center gap-2">
      {node.isSubAssembly && <Layers className="h-3.5 w-3.5 shrink-0 text-info" />}
      <div className="min-w-0">
        <div className={cn('truncate', node.isSubAssembly && 'font-medium')}>
          {item?.name ?? 'Unknown item'}
        </div>
        <div className="flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
          {item?.sku}
          {node.line.scrap_pct > 0 && (
            <span className="font-sans">· +{formatQty(node.line.scrap_pct)}% scrap</span>
          )}
          {node.line.notes && (
            <span className="truncate font-sans" title={node.line.notes}>
              · {node.line.notes}
            </span>
          )}
        </div>
      </div>
      {node.isSubAssembly && (
        <Badge variant="info-soft" className="shrink-0 px-2 py-0 text-[10px]">
          Sub-assembly · {node.children.length}
        </Badge>
      )}
      {node.isCycle && (
        <Badge variant="warning-soft" className="shrink-0">
          Circular
        </Badge>
      )}
    </div>
  );
}

const NUM_HEAD = 'text-right whitespace-nowrap';

// -----------------------------------------------------------------------
// Tree
// -----------------------------------------------------------------------

interface TreeViewProps {
  nodes: BomTreeNode[];
  collapsed: Set<string>;
  onToggle: (key: string) => void;
  /** When set, only these keys (matches + their ancestors) render. */
  visibleKeys: Set<string> | null;
  buildQty: number;
  actions: BomNodeActions;
  totalCents: number;
}

export function BomTreeView({
  nodes,
  collapsed,
  onToggle,
  visibleKeys,
  buildQty,
  actions,
  totalCents,
}: TreeViewProps) {
  const rows: Array<{ node: BomTreeNode; guides: boolean[] }> = [];
  const walk = (list: BomTreeNode[], guides: boolean[]) => {
    list.forEach((node, i) => {
      if (visibleKeys && !visibleKeys.has(node.key)) return;
      rows.push({ node, guides });
      const open = visibleKeys ? true : !collapsed.has(node.key);
      if (node.children.length && open) walk(node.children, [...guides, i < list.length - 1]);
    });
  };
  walk(nodes, []);

  return (
    <div className="overflow-x-auto">
      <Table className="min-w-[860px]">
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">#</TableHead>
            <TableHead>Component</TableHead>
            <TableHead className={cn(NUM_HEAD, 'w-24')}>Qty / unit</TableHead>
            <TableHead className="w-16">UoM</TableHead>
            <TableHead className={cn(NUM_HEAD, 'w-24')}>
              {buildQty === 1 ? 'Ext. qty' : `Qty × ${formatQty(buildQty)}`}
            </TableHead>
            <TableHead className={cn(NUM_HEAD, 'w-24')}>Unit cost</TableHead>
            <TableHead className={cn(NUM_HEAD, 'w-28')}>Ext. cost</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(({ node, guides }) => {
            const open = visibleKeys ? true : !collapsed.has(node.key);
            const expandable = node.children.length > 0;
            return (
              <TableRow
                key={node.key}
                className={cn(
                  node.isSubAssembly && 'bg-secondary/40 hover:bg-secondary/60',
                  expandable && 'cursor-pointer',
                )}
                onClick={() => expandable && onToggle(node.key)}
              >
                <TableCell className="py-1.5 font-mono text-[11px] text-muted-foreground">
                  {node.path}
                </TableCell>
                <TableCell className="py-1.5">
                  <div className="flex items-center">
                    {guides.map((continues, i) => (
                      <span
                        key={i}
                        aria-hidden
                        className={cn(
                          'mr-1 h-9 w-4 shrink-0 self-stretch',
                          continues ? 'border-l border-border' : '',
                        )}
                      />
                    ))}
                    {node.depth > 0 && (
                      <CornerDownRight
                        aria-hidden
                        className="mr-1 h-3.5 w-3.5 shrink-0 text-border"
                      />
                    )}
                    <span className="mr-1 flex h-5 w-5 shrink-0 items-center justify-center">
                      {expandable &&
                        (open ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        ))}
                    </span>
                    <ItemCell node={node} />
                  </div>
                </TableCell>
                <TableCell className="py-1.5 text-right">
                  <QtyCell node={node} canEdit={actions.canEdit} />
                </TableCell>
                <TableCell className="py-1.5 text-muted-foreground">{node.line.uom}</TableCell>
                <TableCell className="py-1.5 text-right tabular-nums">
                  {formatQty(node.extQty)}
                </TableCell>
                <TableCell className="py-1.5 text-right tabular-nums text-muted-foreground">
                  {centsOrDash(node.unitCost.cents, node.unitCost.missing)}
                </TableCell>
                <TableCell className="py-1.5 text-right tabular-nums">
                  {centsOrDash(node.extCostCents, node.unitCost.missing)}
                </TableCell>
                <TableCell className="py-1.5" onClick={(e) => e.stopPropagation()}>
                  <NodeActionsMenu node={node} actions={actions} />
                </TableCell>
              </TableRow>
            );
          })}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">
                No lines match your search.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={6} className="text-right text-sm font-medium">
              Total for {formatQty(buildQty)} unit{buildQty === 1 ? '' : 's'}
            </TableCell>
            <TableCell className="text-right font-medium tabular-nums">
              {formatCurrency(Math.round(totalCents))}
            </TableCell>
            <TableCell />
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
}

// -----------------------------------------------------------------------
// Sections
// -----------------------------------------------------------------------

interface Section {
  key: string;
  owner: BomTreeNode | null;
  lines: BomTreeNode[];
  trail: string[];
}

function buildSections(nodes: BomTreeNode[], rootName: string): Section[] {
  const out: Section[] = [{ key: 'root', owner: null, lines: nodes, trail: [rootName] }];
  const visit = (list: BomTreeNode[], trail: string[]) => {
    for (const n of list) {
      if (n.isSubAssembly && !n.isCycle && n.children.length) {
        const name = n.line.item?.name ?? 'Sub-assembly';
        out.push({ key: n.key, owner: n, lines: n.children, trail: [...trail, name] });
        visit(n.children, [...trail, name]);
      }
    }
  };
  visit(nodes, [rootName]);
  return out;
}

const sectionAnchor = (key: string) => `bom-section-${key.replace(/[^a-zA-Z0-9]/g, '-')}`;

interface SectionsViewProps {
  nodes: BomTreeNode[];
  rootName: string;
  rootSku: string;
  buildQty: number;
  actions: BomNodeActions;
  matches: Set<string> | null;
}

export function BomSectionsView({
  nodes,
  rootName,
  rootSku,
  buildQty,
  actions,
  matches,
}: SectionsViewProps) {
  const sections = buildSections(nodes, rootName).filter(
    (s) =>
      !matches || s.lines.some((l) => matches.has(l.key)) || (s.owner && matches.has(s.owner.key)),
  );

  return (
    <div className="space-y-4">
      {sections.map((section) => {
        const owner = section.owner;
        const sectionCost = section.lines.reduce((acc, l) => acc + l.extCostCents, 0);
        return (
          <Card
            key={section.key}
            id={sectionAnchor(section.key)}
            className={cn('scroll-mt-24 overflow-hidden', owner && 'border-l-4 border-l-info/60')}
            style={owner ? { marginLeft: `${Math.min(owner.depth, 4) * 1.25}rem` } : undefined}
          >
            <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0 border-b border-border bg-secondary/30 py-3">
              <div className="min-w-0 space-y-0.5">
                <div className="flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
                  {section.trail.slice(0, -1).map((t, i) => (
                    <Fragment key={i}>
                      <span className="truncate">{t}</span>
                      <ChevronRight className="h-3 w-3" />
                    </Fragment>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {owner ? (
                    <>
                      <span className="font-mono text-xs text-muted-foreground">{owner.path}</span>
                      <Layers className="h-4 w-4 text-info" />
                      <span className="font-medium">{owner.line.item?.name}</span>
                      <span className="font-mono text-xs text-muted-foreground">
                        {owner.line.item?.sku}
                      </span>
                      <Badge variant="muted">
                        {formatQty(owner.line.quantity)} {owner.line.uom} per parent ·{' '}
                        {formatQty(owner.extQty)} total
                      </Badge>
                    </>
                  ) : (
                    <>
                      <span className="font-medium">Top level</span>
                      <span className="font-mono text-xs text-muted-foreground">{rootSku}</span>
                      <Badge variant="muted">Direct lines on this BOM</Badge>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm tabular-nums text-muted-foreground">
                  {formatCurrency(Math.round(sectionCost))}
                  <span className="text-xs"> for {formatQty(owner ? owner.extQty : buildQty)}</span>
                </span>
                {actions.canEdit && (
                  <Button size="sm" variant="outline" onClick={() => actions.onAddInto(owner)}>
                    <Plus className="h-4 w-4" />
                    Add
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              <Table className="min-w-[760px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16 pl-4">#</TableHead>
                    <TableHead>Component</TableHead>
                    <TableHead className={cn(NUM_HEAD, 'w-24')}>Qty / unit</TableHead>
                    <TableHead className="w-16">UoM</TableHead>
                    <TableHead className={cn(NUM_HEAD, 'w-24')}>Ext. qty</TableHead>
                    <TableHead className={cn(NUM_HEAD, 'w-24')}>Unit cost</TableHead>
                    <TableHead className={cn(NUM_HEAD, 'w-28')}>Ext. cost</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {section.lines.map((node) => (
                    <TableRow
                      key={node.key}
                      className={cn(
                        node.isSubAssembly && 'bg-info-soft/10',
                        matches && !matches.has(node.key) && 'opacity-40',
                      )}
                    >
                      <TableCell className="py-1.5 pl-4 font-mono text-[11px] text-muted-foreground">
                        {node.path}
                      </TableCell>
                      <TableCell className="py-1.5">
                        <div className="flex items-center gap-2">
                          <ItemCell node={node} />
                          {node.isSubAssembly && !node.isCycle && (
                            <a
                              href={`#${sectionAnchor(node.key)}`}
                              className="shrink-0 text-xs font-medium text-info hover:underline"
                            >
                              See section {node.path}
                            </a>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-1.5 text-right">
                        <QtyCell node={node} canEdit={actions.canEdit} />
                      </TableCell>
                      <TableCell className="py-1.5 text-muted-foreground">
                        {node.line.uom}
                      </TableCell>
                      <TableCell className="py-1.5 text-right tabular-nums">
                        {formatQty(node.extQty)}
                      </TableCell>
                      <TableCell className="py-1.5 text-right tabular-nums text-muted-foreground">
                        {centsOrDash(node.unitCost.cents, node.unitCost.missing)}
                      </TableCell>
                      <TableCell className="py-1.5 text-right tabular-nums">
                        {centsOrDash(node.extCostCents, node.unitCost.missing)}
                      </TableCell>
                      <TableCell className="py-1.5">
                        <NodeActionsMenu node={node} actions={actions} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------
// Flat parts list
// -----------------------------------------------------------------------

interface FlatViewProps {
  parts: FlatPart[];
  buildQty: number;
  query: string;
}

export function BomFlatView({ parts, buildQty, query }: FlatViewProps) {
  const q = query.trim().toLowerCase();
  const shown = q
    ? parts.filter(
        (p) =>
          (p.item?.sku ?? '').toLowerCase().includes(q) ||
          (p.item?.name ?? '').toLowerCase().includes(q),
      )
    : parts;
  const total = shown.reduce((acc, p) => acc + p.extCostCents, 0);
  return (
    <div className="overflow-x-auto">
      <Table className="min-w-[760px]">
        <TableHeader>
          <TableRow>
            <TableHead>SKU</TableHead>
            <TableHead>Part</TableHead>
            <TableHead>Pulled into</TableHead>
            <TableHead className={cn(NUM_HEAD, 'w-28')}>
              Total qty{buildQty === 1 ? '' : ` × ${formatQty(buildQty)}`}
            </TableHead>
            <TableHead className="w-16">UoM</TableHead>
            <TableHead className={cn(NUM_HEAD, 'w-24')}>Unit cost</TableHead>
            <TableHead className={cn(NUM_HEAD, 'w-28')}>Ext. cost</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {shown.map((p) => (
            <TableRow key={`${p.itemId}|${p.uom}`}>
              <TableCell className="py-1.5 font-mono text-xs">{p.item?.sku}</TableCell>
              <TableCell className="py-1.5">
                <Link to={`/catalog/items/${p.itemId}`} className="hover:underline">
                  {p.item?.name ?? 'Unknown item'}
                </Link>
              </TableCell>
              <TableCell className="py-1.5 text-xs text-muted-foreground">
                {p.usedUnder.join(', ')}
              </TableCell>
              <TableCell className="py-1.5 text-right tabular-nums">
                {formatQty(p.totalQty)}
              </TableCell>
              <TableCell className="py-1.5 text-muted-foreground">{p.uom}</TableCell>
              <TableCell className="py-1.5 text-right tabular-nums text-muted-foreground">
                {p.unitCostCents == null ? '—' : formatCurrency(p.unitCostCents)}
              </TableCell>
              <TableCell className="py-1.5 text-right tabular-nums">
                {p.unitCostCents == null ? '—' : formatCurrency(Math.round(p.extCostCents))}
              </TableCell>
            </TableRow>
          ))}
          {shown.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                No parts match your search.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={6} className="text-right text-sm font-medium">
              {shown.length} distinct part{shown.length === 1 ? '' : 's'}
            </TableCell>
            <TableCell className="text-right font-medium tabular-nums">
              {formatCurrency(Math.round(total))}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
}
